/**
 * Moteur de calcul de paie
 * Fonctions pures pour calculer les cotisations, brut, net, etc.
 */

import {
  PMSS_2025,
  SMIC_HORAIRE_2025,
  SMIC_MENSUEL_2025,
  COTISATIONS_SALARIE,
  COTISATIONS_EMPLOYEUR,
  TAUX_HEURES_SUP,
  getWeeksInMonth,
  getWorkingDaysInMonth
} from './tables/placeholders';

// --- Helpers arrondis ---
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const pct = (base, rate) => round2((Number(base) * Number(rate)) / 100);

// === Paramètres défaut "Apprenti" (surchargables via cabinet/employee) ===
const APPRENTICE_DEFAULTS = {
  // Base minimale utilisée pour les petites lignes salariées (vieillesse / arrco / CEG)
  employee_min_base: 54.05,           // tu peux ajuster via employee.apprentice_employee_min_base
  // Base CSG apprentis : policy et % du brut (si on n'utilise pas la franchise 79% SMIC)
  csg_policy: 'PCT_BRUT',             // 'PCT_BRUT' | 'FRANCHISE_79'
  csg_pct_of_brut: 0.202,             // 20,2% du brut par défaut, puis abattement 1,75%
};

/**
 * Vérifie si l'employé est apprenti
 * @param {Object} employee
 * @returns {boolean}
 */
export function isApprentice(employee) {
  return employee?.contract_type === 'APPRENTI';
}

/**
 * Calcule l'année d'apprentissage en cours (1, 2 ou 3)
 * @param {Object} employee - Doit contenir start_date et end_date
 * @param {Object} period - { year, month }
 * @returns {number} 1, 2 ou 3
 */
export function getCurrentApprenticeYear(employee, period) {
  if (!employee.start_date) return 1;
  
  const startDate = new Date(employee.start_date);
  const currentDate = new Date(period.year, period.month - 1, 15); // 15 du mois
  
  const monthsDiff = (currentDate.getFullYear() - startDate.getFullYear()) * 12 
                    + currentDate.getMonth() - startDate.getMonth();
  
  if (monthsDiff < 12) return 1;
  if (monthsDiff < 24) return 2;
  return 3;
}

/**
 * Calcule l'assiette CSG/CRDS réduite pour apprentis
 * @param {number} brut
 * @param {Object} employee
 * @param {Object} period
 * @param {Object} cabinet
 * @returns {number}
 */
export function computeApprenticeCsgBase(brut, employee, period, cabinet = {}) {
  // 1) Override explicite si on veut coller à une pièce : ex. employee.csg_base_override = 64.46
  if (employee?.csg_base_override != null) {
    return round2(Math.max(0, parseFloat(employee.csg_base_override)));
  }
  // 2) Politique cabinet (défaut = pourcentage du brut)
  const policy = cabinet.apprentice_csg_policy || APPRENTICE_DEFAULTS.csg_policy;
  if (policy === 'PCT_BRUT') {
    const p = Number(cabinet.apprentice_csg_pct_of_brut ?? APPRENTICE_DEFAULTS.csg_pct_of_brut);
    const baseBrute = round2(Math.max(0, brut * p));
    return round2(baseBrute * 0.9825); // abattement 1,75% puis arrondi base
  }
  if (policy === 'FRANCHISE_79') {
    const franchise = 0.79 * SMIC_MENSUEL_2025;
    const baseBrute = round2(Math.max(0, brut - franchise));
    return round2(baseBrute * 0.9825);
  }
  // Fallback conservateur
  return round2(Math.max(0, brut * 0.20) * 0.9825);
}

/**
 * Calcule les heures de travail du mois
 * @param {Object} params
 * @param {number} params.weeklyHours - Heures hebdo contractuelles
 * @param {number} params.year
 * @param {number} params.month (1-12)
 * @param {Array} params.holidays - Jours fériés du mois
 * @param {Object} params.schoolCfg - Config école apprenti { weekly_hours, school_days }
 * @param {Object} params.overrides - Overrides { hours_overwrite, paid_leave_days, unpaid_leave_days, sick_days }
 * @returns {number}
 */
export function computeWorkingHours({ weeklyHours, year, month, holidays = [], schoolCfg = null, overrides = {} }) {
  // Si override manuel, retourner directement
  if (overrides.hours_overwrite !== undefined && overrides.hours_overwrite !== null && overrides.hours_overwrite !== '') {
    return parseFloat(overrides.hours_overwrite);
  }

  // Calcul base : heures hebdo * nb semaines
  const weeksInMonth = getWeeksInMonth(year, month);
  let baseHours = weeklyHours * weeksInMonth;

  // Pour les apprentis, inclure les heures d'école (= temps de travail)
  if (schoolCfg && schoolCfg.weekly_hours) {
    baseHours += parseFloat(schoolCfg.weekly_hours) * weeksInMonth;
  }

  // Ajustements pour congés/absences
  const hoursPerDay = weeklyHours / 5; // 1 jour = 1/5 des heures hebdo

  // Congés payés : **payés** → aucune déduction d'heures (on les laisse dans la base)
  if (overrides.unpaid_leave_days) {
    baseHours -= parseFloat(overrides.unpaid_leave_days) * hoursPerDay;
  }
  if (overrides.sick_days) {
    baseHours -= parseFloat(overrides.sick_days) * hoursPerDay;
  }

  return Math.max(0, baseHours);
}

/**
 * Calcule le salaire brut
 * @param {Object} params
 * @param {string} params.salaryType - 'MENSUEL' ou 'HORAIRE'
 * @param {number} params.baseSalary - Salaire de base
 * @param {number} params.hours - Heures du mois
 * @param {number} params.workingTimePct - % temps de travail (100 = temps plein)
 * @param {Object} params.overtime - { hours_125, hours_150 }
 * @param {Object} params.benefits - Avantages en nature { vehicle, housing, meals }
 * @returns {number}
 */
export function computeBrut({ salaryType, baseSalary, hours, workingTimePct = 100, overtime = {}, benefits = {}, bonuses = {} }) {
  let brut = 0;

  if (salaryType === 'MENSUEL') {
    // Salaire mensuel proratisé si temps partiel
    brut = baseSalary * (workingTimePct / 100);
    
    // Les heures supp sont des heures **en plus** du forfait mensuel : on ajoute le **montant complet** (base + majoration)
    const tauxHoraire = baseSalary / 151.67;
    if (overtime.hours_125) {
      brut += tauxHoraire * overtime.hours_125 * TAUX_HEURES_SUP.heures_125;
    }
    if (overtime.hours_150) {
      brut += tauxHoraire * overtime.hours_150 * TAUX_HEURES_SUP.heures_150;
    }
  } else if (salaryType === 'HORAIRE') {
    // hours = heures de base **hors** HS ; les HS sont ajoutées ci-dessous
    const tauxHoraire = baseSalary;
    brut = tauxHoraire * hours;
    if (overtime.hours_125) {
      brut += tauxHoraire * overtime.hours_125 * TAUX_HEURES_SUP.heures_125;
    }
    if (overtime.hours_150) {
      brut += tauxHoraire * overtime.hours_150 * TAUX_HEURES_SUP.heures_150;
    }
  }

  // Avantages en nature (ajoutés au brut)
  if (benefits.vehicle) brut += parseFloat(benefits.vehicle);
  if (benefits.housing) brut += parseFloat(benefits.housing);
  if (benefits.meals) brut += parseFloat(benefits.meals);

  // Primes (toutes imposables & soumises cotisations)
  // bonuses = { primeFixe, primeExceptionnelle, primeAnciennete, autresPrime }
  Object.values(bonuses || {}).forEach(v => { if (v) brut += parseFloat(v); });

  // ARRONDIR le brut final au centime
  return Math.round(brut * 100) / 100;
}

/**
 * Calcule les cotisations salariales
 * @param {Object} params
 * @param {number} params.brut
 * @param {string} params.statusCadre - 'CADRE' ou 'NON_CADRE'
 * @param {Object} params.cabinet - Infos cabinet (mutuelle, prévoyance, etc.)
 * @param {Object} params.employee - Infos employé (meal_vouchers, transport, etc.)
 * @returns {Array} Lignes de cotisations { code, label, base, rate, amount }
 */
export function computeEmployeeContribs({ brut, statusCadre = 'NON_CADRE', cabinet = {}, employee = {}, period = {} }) {
  const lines = [];
  const pmss = PMSS_2025;
  const apprentice = isApprentice(employee);

  if (apprentice) {
    // === Bases ===
    const baseMin =
      Number(employee?.apprentice_employee_min_base) ||
      Number(cabinet?.apprentice_employee_min_base) ||
      APPRENTICE_DEFAULTS.employee_min_base;   // ex. 54.05
    const basePetite = round2(baseMin);
    // Base CSG apprentis (arrondie à 2 décimales **avant** application des taux)
    const baseCsg = round2(computeApprenticeCsgBase(brut, employee, period, cabinet));

    // === CSG ===
    lines.push({ code:'CSG_DED',      label:'CSG déductible',             base: baseCsg,    rate: 6.80, amount: pct(baseCsg, 6.80) });
    lines.push({ code:'CSG_ND_CRDS',  label:'CSG non déductible + CRDS',  base: baseCsg,    rate: 2.90, amount: pct(baseCsg, 2.90) });

    // === Petites lignes salariées sur base "petite" ===
    // Vieillesse plafonnée
    lines.push({ code: COTISATIONS_SALARIE.vieillesse_plaf.code,
                 label: COTISATIONS_SALARIE.vieillesse_plaf.label,
                 base: basePetite, rate: COTISATIONS_SALARIE.vieillesse_plaf.rate,
                 amount: pct(basePetite, COTISATIONS_SALARIE.vieillesse_plaf.rate) });
    // Vieillesse déplafonnée
    lines.push({ code: COTISATIONS_SALARIE.vieillesse_deplaf.code,
                 label: COTISATIONS_SALARIE.vieillesse_deplaf.label,
                 base: basePetite, rate: COTISATIONS_SALARIE.vieillesse_deplaf.rate,
                 amount: pct(basePetite, COTISATIONS_SALARIE.vieillesse_deplaf.rate) });
    // Retraite complémentaire T1 (taux paramétrable si besoin)
    const arrcoRate = Number(employee?.arrco_t1_rate_employee) || Number(cabinet?.arrco_t1_rate_employee) || COTISATIONS_SALARIE.retraite_t1.rate; // ex. 4.32
    lines.push({ code: COTISATIONS_SALARIE.retraite_t1.code,
                 label: COTISATIONS_SALARIE.retraite_t1.label,
                 base: basePetite, rate: arrcoRate,
                 amount: pct(basePetite, arrcoRate) });
    // CEG T1
    lines.push({ code: COTISATIONS_SALARIE.ceg_t1.code,
                 label: COTISATIONS_SALARIE.ceg_t1.label,
                 base: basePetite, rate: COTISATIONS_SALARIE.ceg_t1.rate,
                 amount: pct(basePetite, COTISATIONS_SALARIE.ceg_t1.rate) });

    // === IMPORTANT : PAS de mutuelle / PAS de prévoyance / PAS de RCP côté salarié si non configurées ===
    // (Tu as indiqué que la vraie fiche n'en comporte pas : on n'ajoute donc aucune ligne supplémentaire ici.)

    // === (Optionnel) affichage "info" des bases T1 pour cohérence visuelle (montant = 0)
    const baseT1 = round2(Math.min(brut, PMSS_2025));
    lines.push({ code:'SS_PLAF_INFO',   label:'Sécurité sociale plafonnée (info)',   base: baseT1, rate: 0, amount: 0 });
    lines.push({ code:'SS_DEPLAF_INFO', label:'Sécurité sociale déplafonnée (info)', base: round2(Math.max(0, brut - baseT1)), rate: 0, amount: 0 });

    return lines;
  }

  // RÉGIME CLASSIQUE (non-apprenti)
  // Assiette pour CSG/CRDS (98.25% du brut) - ARRONDIR à 2 décimales
  const assietteCsgBrute = brut * COTISATIONS_SALARIE.csg_deductible.assiette;
  const assietteCsg = Math.round(assietteCsgBrute * 100) / 100;

  // Vieillesse plafonnée (Tranche 1)
  const baseT1 = Math.min(brut, pmss);
  const vieillessePlafAmount = Math.round(baseT1 * COTISATIONS_SALARIE.vieillesse_plaf.rate) / 100;
  lines.push({
    code: COTISATIONS_SALARIE.vieillesse_plaf.code,
    label: COTISATIONS_SALARIE.vieillesse_plaf.label,
    base: baseT1,
    rate: COTISATIONS_SALARIE.vieillesse_plaf.rate,
    amount: vieillessePlafAmount
  });

  // Vieillesse déplafonnée
  lines.push({
    code: COTISATIONS_SALARIE.vieillesse_deplaf.code,
    label: COTISATIONS_SALARIE.vieillesse_deplaf.label,
    base: brut,
    rate: COTISATIONS_SALARIE.vieillesse_deplaf.rate,
    amount: brut * COTISATIONS_SALARIE.vieillesse_deplaf.rate / 100
  });

  // Retraite complémentaire Agirc-Arrco T1
  lines.push({
    code: COTISATIONS_SALARIE.retraite_t1.code,
    label: COTISATIONS_SALARIE.retraite_t1.label,
    base: baseT1,
    rate: COTISATIONS_SALARIE.retraite_t1.rate,
    amount: baseT1 * COTISATIONS_SALARIE.retraite_t1.rate / 100
  });

  // Retraite complémentaire T2 (si brut > PMSS)
  if (brut > pmss) {
    const baseT2 = Math.min(brut - pmss, pmss * 7); // T2 = de 1 à 8 PMSS
    lines.push({
      code: COTISATIONS_SALARIE.retraite_t2.code,
      label: COTISATIONS_SALARIE.retraite_t2.label,
      base: baseT2,
      rate: COTISATIONS_SALARIE.retraite_t2.rate,
      amount: baseT2 * COTISATIONS_SALARIE.retraite_t2.rate / 100
    });
  }

  // CEG T1
  lines.push({
    code: COTISATIONS_SALARIE.ceg_t1.code,
    label: COTISATIONS_SALARIE.ceg_t1.label,
    base: baseT1,
    rate: COTISATIONS_SALARIE.ceg_t1.rate,
    amount: baseT1 * COTISATIONS_SALARIE.ceg_t1.rate / 100
  });

  // CEG T2
  if (brut > pmss) {
    const baseT2 = Math.min(brut - pmss, pmss * 7);
    lines.push({
      code: COTISATIONS_SALARIE.ceg_t2.code,
      label: COTISATIONS_SALARIE.ceg_t2.label,
      base: baseT2,
      rate: COTISATIONS_SALARIE.ceg_t2.rate,
      amount: baseT2 * COTISATIONS_SALARIE.ceg_t2.rate / 100
    });
  }

  // CSG déductible
  lines.push({
    code: COTISATIONS_SALARIE.csg_deductible.code,
    label: COTISATIONS_SALARIE.csg_deductible.label,
    base: assietteCsg,
    rate: COTISATIONS_SALARIE.csg_deductible.rate,
    amount: assietteCsg * COTISATIONS_SALARIE.csg_deductible.rate / 100
  });

  // CSG non déductible
  lines.push({
    code: COTISATIONS_SALARIE.csg_non_deductible.code,
    label: COTISATIONS_SALARIE.csg_non_deductible.label,
    base: assietteCsg,
    rate: COTISATIONS_SALARIE.csg_non_deductible.rate,
    amount: assietteCsg * COTISATIONS_SALARIE.csg_non_deductible.rate / 100
  });

  // CRDS
  lines.push({
    code: COTISATIONS_SALARIE.crds.code,
    label: COTISATIONS_SALARIE.crds.label,
    base: assietteCsg,
    rate: COTISATIONS_SALARIE.crds.rate,
    amount: assietteCsg * COTISATIONS_SALARIE.crds.rate / 100
  });

  // Mutuelle (part salariale)
  if (employee.mutuelle_premium_total && employee.mutuelle_employer_share_percent) {
    const totalPremium = parseFloat(employee.mutuelle_premium_total);
    const employerPercent = parseFloat(employee.mutuelle_employer_share_percent);
    const employeePart = totalPremium * (1 - employerPercent / 100);
    
    if (employeePart > 0) {
      lines.push({
        code: 'MUT_SAL',
        label: `Mutuelle (${employee.mutuelle_name || 'Complémentaire santé'})`,
        base: totalPremium,
        rate: (100 - employerPercent).toFixed(2),
        amount: employeePart
      });
    }
  }

  // Prévoyance (part salariale - selon mode FORFAIT ou PERCENT)
  if (cabinet.prevoyance_mode === 'FORFAIT' && cabinet.prevoyance_premium_total && cabinet.prevoyance_employer_share_percent) {
    const totalPremium = parseFloat(cabinet.prevoyance_premium_total);
    const employerPercent = parseFloat(cabinet.prevoyance_employer_share_percent);
    const employeePart = totalPremium * (1 - employerPercent / 100);
    
    if (employeePart > 0) {
      lines.push({
        code: 'PREV_SAL',
        label: `Prévoyance (${cabinet.prevoyance_name || 'Prévoyance'})`,
        base: totalPremium,
        rate: (100 - employerPercent).toFixed(2),
        amount: employeePart
      });
    }
  } else if (cabinet.prevoyance_mode === 'PERCENT') {
    // TODO: Implémenter calcul T1/T2 avec taux salarié/employeur
    // Pour l'instant, placeholder
  }

  // Titres-restaurant (part salariale)
  if (employee.meal_vouchers_enabled && employee.meal_voucher_value && employee.meal_voucher_employer_share_percent) {
    const daysEligible = employee.meal_voucher_days_per_month || 18;
    const voucherValue = parseFloat(employee.meal_voucher_value);
    const employerPercent = parseFloat(employee.meal_voucher_employer_share_percent);
    const employeePart = daysEligible * voucherValue * (1 - employerPercent / 100);
    
    if (employeePart > 0) {
      lines.push({
        code: 'TR_SAL',
        label: 'Titres-restaurant (part salariale)',
        base: daysEligible * voucherValue,
        rate: (100 - employerPercent).toFixed(2),
        amount: employeePart
      });
    }
  }

  // Transport (part salariale)
  if (employee.transport_enabled && employee.transport_monthly_cost && employee.transport_employer_share_percent) {
    const monthlyCost = parseFloat(employee.transport_monthly_cost);
    const employerPercent = parseFloat(employee.transport_employer_share_percent);
    const employeePart = monthlyCost * (1 - employerPercent / 100);
    
    if (employeePart > 0) {
      lines.push({
        code: 'TRANS_SAL',
        label: 'Transport (part salariale)',
        base: monthlyCost,
        rate: (100 - employerPercent).toFixed(2),
        amount: employeePart
      });
    }
  }

  return lines;
}

/**
 * Calcule l'exonération employeur pour apprentis (sur périmètre éligible)
 * Par défaut on exempte une large partie des cotisations URSSAF employeur,
 * MAIS on garde certaines lignes dues (AT/MP, formation pro, taxe apprentissage, mutuelle/prévoyance/TR/transport…).
 * @param {Array} lines - Lignes de cotisations employeur
 * @returns {number} Montant à exonérer
 */
function computeEmployerApprenticeExo(lines) {
  const keep = new Set([
    'ATMP', 
    COTISATIONS_EMPLOYEUR.formation_pro.code, 
    COTISATIONS_EMPLOYEUR.taxe_apprentissage.code,
    'MUT_EMP','PREV_EMP','TR_EMP','TRANS_EMP'
  ].map(String));
  let eligible = 0;
  for (const l of lines) {
    const code = String(l.code || '').toUpperCase();
    if (!keep.has(code)) eligible += Number(l.amount || 0);
  }
  return round2(eligible);
}

/**
 * Calcule les cotisations patronales
 * @param {Object} params
 * @param {number} params.brut
 * @param {number} params.atmpRate - Taux AT/MP du cabinet (%)
 * @param {string} params.statusCadre
 * @param {Object} params.cabinet
 * @param {Object} params.employee
 * @returns {Array} Lignes de cotisations employeur
 */
export function computeEmployerContribs({ brut, atmpRate, statusCadre = 'NON_CADRE', cabinet = {}, employee = {} }) {
  const lines = [];
  const pmss = PMSS_2025;

  // Allocations familiales (taux réduit si brut < 3.5 SMIC)
  const seuilReduit = 3.5 * SMIC_MENSUEL_2025;
  const tauxAf = brut < seuilReduit ? COTISATIONS_EMPLOYEUR.allocations_familiales_reduit.rate : COTISATIONS_EMPLOYEUR.allocations_familiales.rate;
  lines.push({
    code: brut < seuilReduit ? COTISATIONS_EMPLOYEUR.allocations_familiales_reduit.code : COTISATIONS_EMPLOYEUR.allocations_familiales.code,
    label: brut < seuilReduit ? COTISATIONS_EMPLOYEUR.allocations_familiales_reduit.label : COTISATIONS_EMPLOYEUR.allocations_familiales.label,
    base: brut,
    rate: tauxAf,
    amount: brut * tauxAf / 100
  });

  // Assurance maladie
  lines.push({
    code: COTISATIONS_EMPLOYEUR.maladie.code,
    label: COTISATIONS_EMPLOYEUR.maladie.label,
    base: brut,
    rate: COTISATIONS_EMPLOYEUR.maladie.rate,
    amount: brut * COTISATIONS_EMPLOYEUR.maladie.rate / 100
  });

  // AT/MP (taux spécifique du cabinet)
  if (atmpRate) {
    lines.push({
      code: 'ATMP',
      label: 'Accidents du travail / Maladies professionnelles',
      base: brut,
      rate: parseFloat(atmpRate),
      amount: brut * parseFloat(atmpRate) / 100
    });
  }

  // Solidarité autonomie
  lines.push({
    code: COTISATIONS_EMPLOYEUR.solidarite.code,
    label: COTISATIONS_EMPLOYEUR.solidarite.label,
    base: brut,
    rate: COTISATIONS_EMPLOYEUR.solidarite.rate,
    amount: brut * COTISATIONS_EMPLOYEUR.solidarite.rate / 100
  });

  // Vieillesse plafonnée
  const baseT1 = Math.min(brut, pmss);
  lines.push({
    code: COTISATIONS_EMPLOYEUR.vieillesse_plaf.code,
    label: COTISATIONS_EMPLOYEUR.vieillesse_plaf.label,
    base: baseT1,
    rate: COTISATIONS_EMPLOYEUR.vieillesse_plaf.rate,
    amount: baseT1 * COTISATIONS_EMPLOYEUR.vieillesse_plaf.rate / 100
  });

  // Vieillesse déplafonnée
  lines.push({
    code: COTISATIONS_EMPLOYEUR.vieillesse_deplaf.code,
    label: COTISATIONS_EMPLOYEUR.vieillesse_deplaf.label,
    base: brut,
    rate: COTISATIONS_EMPLOYEUR.vieillesse_deplaf.rate,
    amount: brut * COTISATIONS_EMPLOYEUR.vieillesse_deplaf.rate / 100
  });

  // Assurance chômage
  const baseChomage = Math.min(brut, pmss * 4);
  lines.push({
    code: COTISATIONS_EMPLOYEUR.chomage.code,
    label: COTISATIONS_EMPLOYEUR.chomage.label,
    base: baseChomage,
    rate: COTISATIONS_EMPLOYEUR.chomage.rate,
    amount: baseChomage * COTISATIONS_EMPLOYEUR.chomage.rate / 100
  });

  // AGS
  lines.push({
    code: COTISATIONS_EMPLOYEUR.ags.code,
    label: COTISATIONS_EMPLOYEUR.ags.label,
    base: baseChomage,
    rate: COTISATIONS_EMPLOYEUR.ags.rate,
    amount: baseChomage * COTISATIONS_EMPLOYEUR.ags.rate / 100
  });

  // Retraite complémentaire Agirc-Arrco T1
  lines.push({
    code: COTISATIONS_EMPLOYEUR.retraite_t1.code,
    label: COTISATIONS_EMPLOYEUR.retraite_t1.label,
    base: baseT1,
    rate: COTISATIONS_EMPLOYEUR.retraite_t1.rate,
    amount: baseT1 * COTISATIONS_EMPLOYEUR.retraite_t1.rate / 100
  });

  // Retraite complémentaire T2 (si brut > PMSS)
  if (brut > pmss) {
    const baseT2 = Math.min(brut - pmss, pmss * 7);
    lines.push({
      code: COTISATIONS_EMPLOYEUR.retraite_t2.code,
      label: COTISATIONS_EMPLOYEUR.retraite_t2.label,
      base: baseT2,
      rate: COTISATIONS_EMPLOYEUR.retraite_t2.rate,
      amount: baseT2 * COTISATIONS_EMPLOYEUR.retraite_t2.rate / 100
    });
  }

  // CEG T1
  lines.push({
    code: COTISATIONS_EMPLOYEUR.ceg_t1.code,
    label: COTISATIONS_EMPLOYEUR.ceg_t1.label,
    base: baseT1,
    rate: COTISATIONS_EMPLOYEUR.ceg_t1.rate,
    amount: baseT1 * COTISATIONS_EMPLOYEUR.ceg_t1.rate / 100
  });

  // CEG T2
  if (brut > pmss) {
    const baseT2 = Math.min(brut - pmss, pmss * 7);
    lines.push({
      code: COTISATIONS_EMPLOYEUR.ceg_t2.code,
      label: COTISATIONS_EMPLOYEUR.ceg_t2.label,
      base: baseT2,
      rate: COTISATIONS_EMPLOYEUR.ceg_t2.rate,
      amount: baseT2 * COTISATIONS_EMPLOYEUR.ceg_t2.rate / 100
    });
  }

  // Formation professionnelle
  lines.push({
    code: COTISATIONS_EMPLOYEUR.formation_pro.code,
    label: COTISATIONS_EMPLOYEUR.formation_pro.label,
    base: brut,
    rate: COTISATIONS_EMPLOYEUR.formation_pro.rate,
    amount: brut * COTISATIONS_EMPLOYEUR.formation_pro.rate / 100
  });

  // Taxe d'apprentissage
  lines.push({
    code: COTISATIONS_EMPLOYEUR.taxe_apprentissage.code,
    label: COTISATIONS_EMPLOYEUR.taxe_apprentissage.label,
    base: brut,
    rate: COTISATIONS_EMPLOYEUR.taxe_apprentissage.rate,
    amount: brut * COTISATIONS_EMPLOYEUR.taxe_apprentissage.rate / 100
  });

  // Mutuelle (part employeur)
  if (employee.mutuelle_premium_total && employee.mutuelle_employer_share_percent) {
    const totalPremium = parseFloat(employee.mutuelle_premium_total);
    const employerPercent = parseFloat(employee.mutuelle_employer_share_percent);
    const employerPart = totalPremium * (employerPercent / 100);
    
    if (employerPart > 0) {
      lines.push({
        code: 'MUT_EMP',
        label: `Mutuelle part employeur (${employee.mutuelle_name || 'Complémentaire santé'})`,
        base: totalPremium,
        rate: employerPercent.toFixed(2),
        amount: employerPart
      });
    }
  }

  // Prévoyance (part employeur - mode FORFAIT)
  if (cabinet.prevoyance_mode === 'FORFAIT' && cabinet.prevoyance_premium_total && cabinet.prevoyance_employer_share_percent) {
    const totalPremium = parseFloat(cabinet.prevoyance_premium_total);
    const employerPercent = parseFloat(cabinet.prevoyance_employer_share_percent);
    const employerPart = totalPremium * (employerPercent / 100);
    
    if (employerPart > 0) {
      lines.push({
        code: 'PREV_EMP',
        label: `Prévoyance part employeur (${cabinet.prevoyance_name || 'Prévoyance'})`,
        base: totalPremium,
        rate: employerPercent.toFixed(2),
        amount: employerPart
      });
    }
  }

  // Titres-restaurant (part employeur)
  if (employee.meal_vouchers_enabled && employee.meal_voucher_value && employee.meal_voucher_employer_share_percent) {
    const daysEligible = employee.meal_voucher_days_per_month || 18;
    const voucherValue = parseFloat(employee.meal_voucher_value);
    const employerPercent = parseFloat(employee.meal_voucher_employer_share_percent);
    const employerPart = daysEligible * voucherValue * (employerPercent / 100);
    
    if (employerPart > 0) {
      lines.push({
        code: 'TR_EMP',
        label: 'Titres-restaurant part employeur',
        base: daysEligible * voucherValue,
        rate: employerPercent.toFixed(2),
        amount: employerPart
      });
    }
  }

  // Transport (part employeur)
  if (employee.transport_enabled && employee.transport_monthly_cost && employee.transport_employer_share_percent) {
    const monthlyCost = parseFloat(employee.transport_monthly_cost);
    const employerPercent = parseFloat(employee.transport_employer_share_percent);
    const employerPart = monthlyCost * (employerPercent / 100);
    
    if (employerPart > 0) {
      lines.push({
        code: 'TRANS_EMP',
        label: 'Transport part employeur',
        base: monthlyCost,
        rate: employerPercent.toFixed(2),
        amount: employerPart
      });
    }
  }

  // EXONÉRATION APPRENTIS (ligne négative)
  if (isApprentice(employee)) {
    if (cabinet?.exo_apprenti_override_amount != null) {
      lines.push({
        code: 'EXO_APP',
        label: 'Exonérations de cotisations employeur',
        base: brut,
        rate: null,
        amount: -Math.abs(parseFloat(cabinet.exo_apprenti_override_amount))
      });
    } else {
      const eligibleSum = computeEmployerApprenticeExo(lines);
      lines.push({
        code: 'EXO_APP',
        label: 'Exonérations de cotisations employeur',
        base: brut,
        rate: null,
        amount: -Math.abs(eligibleSum)
      });
    }
  }

  return lines;
}

/**
 * Calcule le net à payer
 * @param {Object} params
 * @param {number} params.brut
 * @param {Array} params.employeeContribs - Lignes cotisations salariales
 * @param {number} params.withholdingRate - Taux PAS (ex: 0.10 pour 10%)
 * @returns {Object} { net_imposable, pas, net_a_payer }
 */
export function computeNet({ brut, employeeContribs, withholdingRate = 0 }) {
  // Somme des cotisations salariales
  const totalSalarie = employeeContribs.reduce((sum, line) => sum + line.amount, 0);
  
  // Net imposable = Brut - cotisations (sauf CSG/CRDS non déductibles)
  // Support ligne fusionnée CSG_ND_CRDS pour apprentis
  const csgNonDeductible = employeeContribs
    .filter(line => ['CSG_ND', 'CRDS', 'CSG_ND_CRDS'].includes(line.code))
    .reduce((sum, line) => sum + line.amount, 0);
  
  const netImposable = brut - totalSalarie + csgNonDeductible;
  
  // PAS (Prélèvement à la source)
  const pas = netImposable * parseFloat(withholdingRate);
  
  // Net à payer
  const netAPayer = brut - totalSalarie - pas;
  
  return {
    net_imposable: netImposable,
    pas: pas,
    net_a_payer: netAPayer
  };
}

/**
 * Compose la fiche de paie complète
 * @param {Object} params
 * @param {Object} params.cabinet
 * @param {Object} params.employee
 * @param {Object} params.period - { year, month }
 * @param {Object} params.calculations - Résultats des calculs
 * @returns {Object} Fiche de paie complète
 */
export function composePayslip({ cabinet, employee, period, calculations }) {
  const { brut, employeeContribs, employerContribs, netResult } = calculations;
  
  const totalEmployeeContribs = employeeContribs.reduce((sum, line) => sum + line.amount, 0);
  const totalEmployerContribs = employerContribs.reduce((sum, line) => sum + line.amount, 0);
  
  return {
    // Métadonnées
    period,
    generatedAt: new Date().toISOString(),
    
    // Employeur
    employer: {
      raison_sociale: cabinet.raison_sociale,
      siret: cabinet.siret,
      naf: cabinet.naf,
      idcc: cabinet.convention_idcc,
      address: cabinet.adresse
    },
    
    // Salarié
    employee: {
      first_name: employee.first_name,
      last_name: employee.last_name,
      nir_last4: employee.nir_last4,
      job_title: employee.job_title,
      contract_type: employee.contract_type,
      classification: {
        level: employee.classification_level,
        echelon: employee.classification_echelon,
        coefficient: employee.classification_coefficient
      },
      status_cadre: employee.status_cadre
    },
    
    // Calculs intermédiaires (pour affichage)
    calculations: {
      hours: calculations.hours,
      brut: calculations.brut
    },
    
    // Lignes de paie
    lines: {
      employee: employeeContribs,
      employer: employerContribs
    },
    
    // Totaux
    totals: {
      brut: brut,
      total_employee_contribs: totalEmployeeContribs,
      total_employer_contribs: totalEmployerContribs,
      net_imposable: netResult.net_imposable,
      pas: netResult.pas,
      net_a_payer: netResult.net_a_payer,
      cout_total: brut + totalEmployerContribs
    }
  };
}
